/* eslint-disable jsx-a11y/no-autofocus */
import { EditOutlined, PlusCircleOutlined, UserOutlined } from '@ant-design/icons/lib'
import { Divider, Row } from 'antd'
import CheckoutDrawer from 'app/components/Core/Checkout/Drawer'
import { CheckoutFooter } from 'app/components/Core/Checkout/Footer'
import { MemberProps } from 'app/components/Core/convention'
import KSearch from 'app/components/Modules/KSearch'
import KList from 'app/components/Modules/List/KList'
import KListItem from 'app/components/Modules/List/KListItem'
import Button from 'app/components/UI-Elements/General/Button'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { RecordType } from 'app/constants/GlobalTypes'
import { useBrowserRouterContext } from 'app/lib/contexts/KBrowserRouter'
import { useIndex } from 'app/lib/hooks/useApi'
import { useMutation } from 'app/lib/hooks/useMutation'
import useTranslation from 'app/lib/hooks/useTranslation'
import { RecordTypeEnum } from 'app/queries'
import * as rentalsCheckout from 'app/queries/Rentals/Checkout'
import React, { useEffect, useState } from 'react'

import Create from './Create'
import Update from './Update'

interface Props {
  onSuccess?: () => void
}

const Index: React.FC<MemberProps<IRental, Props>> = ({
  member: rental,
  props: { onSuccess } = {}
}) => {
  const {
    loading,
    execute,
    response: { data }
  } = useIndex<IContact>(RecordType.contact)
  const { loading: updating, execute: update } = useMutation(
    rentalsCheckout.UPDATE_DELIVERY_CONTACT,
    {
      recordType: RecordTypeEnum.rental,
      silentSuccess: true
    }
  )
  const [q, setQ] = useState<string>(null)
  const [createVisible, setCreateVisible] = useState<boolean>(false)
  const [selectedContactId, setSelectedContactId] = useState<number>(null)
  const { tRecordType, tButtonTitle } = useTranslation({
    recordType: RecordTypeEnum.rental
  })
  const { removeLast } = useBrowserRouterContext()

  useEffect(() => {
    execute({ sort: 'first_name' })
  }, [])

  useEffect(() => {
    if (q !== null) {
      execute({ q, sort: 'first_name' })
    }
  }, [q])

  const onSelect = async (delivery: IContact) => {
    await update({ rentalId: rental?.id, deliveryContactId: delivery.id })
    removeLast()
    onSuccess?.()
  }

  const onCreate = () => setCreateVisible(true)

  const onUpdate = (e: React.MouseEvent<HTMLElement, MouseEvent>, item: IContact) => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedContactId(item.id)
  }

  const closeUpdateContactDrawer = () => {
    removeLast()
    setSelectedContactId(null)
  }

  const closeCreateContactDrawer = () => {
    removeLast()
    setCreateVisible(false)
  }

  const onItemClick = (item: IContact) => onSelect(item)

  const renderItem = (item: IContact, onClick?: (item: IContact) => void) => (
    <KListItem
      extra={<Button onClick={(e) => onUpdate(e, item)} icon={<EditOutlined />} type="secondary" />}
      onClick={onClick ? () => onClick(item) : undefined}
      avatar={<UserOutlined className="font-size-22" />}
      title={item.name}
      verticalAlign
      description={item.tel}
    />
  )

  return (
    <Row>
      <KCol span={24}>
        <Row gutter={[0, 12]} className="mobile-spacing">
          <KCol span={24}>
            <KSearch
              loading={loading}
              recordType={RecordType.delivery_contact}
              onSearch={setQ}
              searchDelay={1000}
              autoFocus
              showLabel
            />
          </KCol>
          <KCol className="text-center" span={24}>
            <Button
              onClick={onCreate}
              icon={<PlusCircleOutlined />}
              type="secondary"
              title={tButtonTitle({
                id: 'add_delivery_contact'
              })}
            />
          </KCol>
          <KCol span={24} />
          <KCol span={24} />
          <If condition={rental?.deliveryContact}>
            <KCol span={24}>
              <span>{tRecordType({ id: 'existing_delivery_contact' })}</span>
            </KCol>
            <KCol span={24}>
              <Divider className="my-0" />
              <KList
                pagination={false}
                dataSource={[rental.deliveryContact]}
                renderItem={(item) => renderItem(item, onItemClick)}
              />
              <Divider className="my-0" />
            </KCol>
            <KCol span={24} />
          </If>
          <KCol span={24}>
            <span>{tRecordType({ id: 'delivery_contact' })}</span>
          </KCol>
          <KCol span={24}>
            <Divider className="my-0" />
            <KList
              pagination={false}
              showTotal={false}
              dataSource={data}
              loading={loading || updating}
              renderItem={(item) => renderItem(item, onItemClick)}
            />
            <Divider className="my-0" />
          </KCol>
        </Row>
        <CheckoutDrawer
          title={tRecordType({ id: 'contact_details' })}
          open={createVisible}
          onClose={closeCreateContactDrawer}
          footer={<CheckoutFooter />}>
          <Create
            rental_id={rental?.id}
            onSuccess={() => {
              closeCreateContactDrawer()
              onSuccess?.()
            }}
          />
        </CheckoutDrawer>
        <CheckoutDrawer
          title={tRecordType({ id: 'contact_details' })}
          open={Boolean(selectedContactId)}
          onClose={closeUpdateContactDrawer}
          footer={<CheckoutFooter />}>
          <Update
            rental_id={rental?.id}
            onClose={closeUpdateContactDrawer}
            contact_id={selectedContactId}
            onSuccess={() => {
              closeUpdateContactDrawer()
              onSuccess?.()
            }}
          />
        </CheckoutDrawer>
      </KCol>
    </Row>
  )
}

export default Index
